import React, { useEffect, useRef, useState } from 'react';

// Gemeinsame Helfer der Admin-Seite (Formatierung, Pagination, Tabellen-Styles).

export const PERIODS = ['TODAY', 'THIS_MONTH', 'LAST_MONTH'] as const;
export const PERIOD_LABELS: Record<string, string> = {
  TODAY: 'Heute', THIS_MONTH: 'Dieser Monat', LAST_MONTH: 'Letzter Monat',
};

export const TH: React.CSSProperties = {
  background: 'var(--bs-tertiary-bg)', color: 'var(--bs-secondary-color)',
  fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.05em',
};

export function fmtDate(iso: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function fmtDuration(ms: number): string {
  const s = Math.round(Math.abs(ms) / 1000);
  if (s < 90) return `${s}s`;
  return `${Math.round(s / 60)} Min.`;
}

export function timeRemaining(expiresAt: string): { label: string; color: string } {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return { label: 'Abgelaufen', color: 'var(--bs-danger)' };
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const label = h > 0 ? `${h}h ${m}m` : `${m}m`;
  const color = ms > 3600000 ? 'var(--bs-success)' : ms > 900000 ? 'var(--bs-warning)' : 'var(--bs-danger)';
  return { label, color };
}

export function parseUA(ua: string): string {
  if (!ua) return '—';
  if (/Edg\//.test(ua)) return 'Edge';
  if (/OPR\//.test(ua)) return 'Opera';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Chrome\//.test(ua)) return 'Chrome';
  if (/Safari\//.test(ua)) return 'Safari';
  return ua.slice(0, 24) + (ua.length > 24 ? '…' : '');
}

// ── Pagination ────────────────────────────────────────────────────────────────

export const PAGE_SIZES = [10, 25, 50, 100];

export function usePaginated<T>(items: T[], defaultSize = 25) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultSize);
  const totalPages = Math.max(1, Math.ceil(items.length / pageSize));
  const clampedPage = Math.min(page, totalPages - 1);

  const prevItemsRef = useRef(items);
  useEffect(() => {
    if (prevItemsRef.current !== items) { setPage(0); prevItemsRef.current = items; }
  }, [items]);

  const slice = items.slice(clampedPage * pageSize, (clampedPage + 1) * pageSize);
  return {
    slice,
    page: clampedPage,
    setPage,
    totalPages,
    pageSize,
    setPageSize: (s: number) => { setPageSize(s); setPage(0); },
    total: items.length,
  };
}

interface PaginationBarProps {
  page: number; totalPages: number; pageSize: number; total: number;
  onPage: (p: number) => void; onPageSize: (s: number) => void;
}

export function PaginationBar({ page, totalPages, pageSize, total, onPage, onPageSize }: PaginationBarProps) {
  return (
    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 pt-2 border-top" style={{ fontSize: '0.82rem' }}>
      <div className="d-flex align-items-center gap-2 text-muted">
        <select
          className="form-select form-select-sm"
          style={{ width: 'auto' }}
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
        >
          {PAGE_SIZES.map((s) => <option key={s} value={s}>{s} pro Seite</option>)}
        </select>
        <span>{total} gesamt</span>
      </div>
      {totalPages > 1 && (
        <div className="d-flex align-items-center gap-1">
          <button className="btn btn-sm btn-outline-secondary py-0 px-2" onClick={() => onPage(0)} disabled={page === 0}>«</button>
          <button className="btn btn-sm btn-outline-secondary py-0 px-2" onClick={() => onPage(page - 1)} disabled={page === 0}>‹</button>
          <span className="px-2">Seite {page + 1} / {totalPages}</span>
          <button className="btn btn-sm btn-outline-secondary py-0 px-2" onClick={() => onPage(page + 1)} disabled={page >= totalPages - 1}>›</button>
          <button className="btn btn-sm btn-outline-secondary py-0 px-2" onClick={() => onPage(totalPages - 1)} disabled={page >= totalPages - 1}>»</button>
        </div>
      )}
    </div>
  );
}

// ── Bestätigungs-Dialog ───────────────────────────────────────────────────────

interface ConfirmProps {
  title: string;
  text?: React.ReactNode;
  confirmLabel?: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({ title, text, confirmLabel = 'Löschen', danger = true, busy, onConfirm, onCancel }: ConfirmProps) {
  return (
    <div className="modal d-block" style={{ background: 'rgba(0,0,0,0.45)' }}>
      <div className="modal-dialog modal-sm modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-body text-center py-4">
            <p className={`mb-1 fw-semibold ${danger ? 'text-danger' : ''}`}>{title}</p>
            {text && <p className="small text-muted mb-3">{text}</p>}
            <div className="d-flex gap-2 justify-content-center mt-2">
              <button className={`btn btn-sm ${danger ? 'btn-danger' : 'btn-primary'}`} disabled={busy} onClick={onConfirm}>{confirmLabel}</button>
              <button className="btn btn-outline-secondary btn-sm" onClick={onCancel}>Abbrechen</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
